/**
 * Rebuy Smart Cart (v1) add-ons, ported from the live theme so the revamp cart works like live:
 * - free shipping tiers: retargets Rebuy's free shipping bar between the two tier thresholds
 * - gift message: shows the gift messages for carts with an eligible product, removes the gift when not eligible
 * - gift with purchase: on the cart page, reloads when Rebuy adds or removes the gift so the page lists it
 * - membership-only cart: hides the progress bars when the cart only holds the membership
 *
 * Live sources: free-shipping-rebuy, rebuy-cart-gift, rebuy-gift-with-purchase (snippets), rebuy-membership-cart.js.
 * Settings and copy come from the JSON in <rebuy-cart-addons> (snippets/component-rebuy-cart.liquid).
 */

/**
 * @typedef {Object} RebuyCartConfig
 * @property {{ firstTier: number, secondTier: number, remainFirst: string, remainSecond: string, reachedSecond: string }} freeShipping
 * @property {{ eligibleTag: string, giftSku: string, message: string, inCartMessage: string }} giftMessage
 * @property {string} giftWithPurchaseSku
 * @property {string} membershipProductId
 * @property {boolean} isCartPage
 */

/**
 * @typedef {Object} CartLine
 * @property {string|number} [variant_id]
 * @property {string|number} [product_id]
 * @property {string} [sku]
 * @property {string|string[]} [tags]
 * @property {string|string[]} [product_tags]
 * @property {{ id?: string|number, tags?: string|string[] }} [product]
 */

/** Delays copied from live: Rebuy re-renders after its events, so the bar and gift checks run once it settles. */
const REBUY_SETTLE_MS = 1000;
const BAR_FADE_MS = 500;
const BAR_FADE_FIRST_TIER_MS = 100;
const CART_PAGE_THEME_SETTLE_MS = 500;
const CART_PAGE_RELOAD_MS = 300;

if (!customElements.get('rebuy-cart-addons')) {
  class RebuyCartAddons extends HTMLElement {
    /** @type {RebuyCartConfig|null} */
    config = null;
    /** Cart page only: whether the gift with purchase was in the cart at the last check (null until the first) */
    giftWithPurchaseInCart = null;
    abortController = undefined;
    timers = new Set();
    /** Rebuy's own free shipping copy, cached before the first tier swap */
    rebuyShippingCopy = null;

    connectedCallback() {
      this.config = this.readConfig();
      if (!this.config) return;

      this.onSmartCartReady = this.onSmartCartReady.bind(this);
      this.onSmartCartShow = this.onSmartCartShow.bind(this);
      this.onRebuyCartChange = this.onRebuyCartChange.bind(this);
      this.onLineItemRemovedOrDecreased = this.onLineItemRemovedOrDecreased.bind(this);
      this.onLineItemIncreased = this.onLineItemIncreased.bind(this);
      this.onThemeCartChange = this.onThemeCartChange.bind(this);

      document.addEventListener('rebuy:smartcart.ready', this.onSmartCartReady);
      document.addEventListener('rebuy:smartcart.show', this.onSmartCartShow);
      document.addEventListener('rebuy:cart.change', this.onRebuyCartChange);
      document.addEventListener('rebuy:smartcart.line-item-removed', this.onLineItemRemovedOrDecreased);
      document.addEventListener('rebuy:smartcart.line-item-decrease', this.onLineItemRemovedOrDecreased);
      document.addEventListener('rebuy:smartcart.line-item-increase', this.onLineItemIncreased);
      document.addEventListener('cart:change', this.onThemeCartChange);

      this.updateMembershipOnly(window.Cart?.state?.items);
      if (this.config.isCartPage) this.checkGiftWithPurchase(REBUY_SETTLE_MS);
    }

    disconnectedCallback() {
      document.removeEventListener('rebuy:smartcart.ready', this.onSmartCartReady);
      document.removeEventListener('rebuy:smartcart.show', this.onSmartCartShow);
      document.removeEventListener('rebuy:cart.change', this.onRebuyCartChange);
      document.removeEventListener('rebuy:smartcart.line-item-removed', this.onLineItemRemovedOrDecreased);
      document.removeEventListener('rebuy:smartcart.line-item-decrease', this.onLineItemRemovedOrDecreased);
      document.removeEventListener('rebuy:smartcart.line-item-increase', this.onLineItemIncreased);
      document.removeEventListener('cart:change', this.onThemeCartChange);

      for (const timer of this.timers) clearTimeout(timer);
      this.timers.clear();
      this.abortController?.abort();
    }

    /** @returns {RebuyCartConfig|null} */
    readConfig() {
      const json = this.querySelector('script[type="application/json"]')?.textContent;
      if (!json) return null;

      try {
        return JSON.parse(json);
      } catch (error) {
        console.error('rebuy-cart-addons: invalid config', error);
        return null;
      }
    }

    /**
     * setTimeout that is cleared when the element disconnects.
     * @param {Function} callback
     * @param {number} delay
     */
    later(callback, delay) {
      const timer = setTimeout(() => {
        this.timers.delete(timer);
        callback();
      }, delay);
      this.timers.add(timer);
    }

    /** @returns {CartLine[]} Rebuy's view of the cart (it changes the cart with its own requests) */
    get rebuyItems() {
      return window.Rebuy?.Cart?.cart?.items || [];
    }

    onSmartCartReady() {
      this.updateGiftMessage();
      this.updateMembershipOnly(this.rebuyItems);
    }

    onSmartCartShow() {
      this.updateFreeShippingBar();
      this.updateGiftMessage();
      this.updateMembershipOnly(this.rebuyItems);
    }

    /** @param {CustomEvent} event */
    onRebuyCartChange(event) {
      this.updateFreeShippingBar();
      this.updateMembershipOnly(event.detail?.cart?.cart?.items || this.rebuyItems);
      if (this.config.isCartPage) this.checkGiftWithPurchase(REBUY_SETTLE_MS);
    }

    onLineItemRemovedOrDecreased() {
      this.later(() => this.updateGiftMessage(), REBUY_SETTLE_MS);
      if (this.config.isCartPage) this.checkGiftWithPurchase(REBUY_SETTLE_MS);
    }

    onLineItemIncreased() {
      this.updateGiftMessage();
      if (this.config.isCartPage) this.checkGiftWithPurchase(REBUY_SETTLE_MS);
    }

    /** @param {CustomEvent} event - cart.js 'cart:change' */
    onThemeCartChange(event) {
      this.updateMembershipOnly(event.detail?.cart?.items);
      if (this.config.isCartPage) this.checkGiftWithPurchase(CART_PAGE_THEME_SETTLE_MS);
    }

    /* ----- free shipping tiers ----- */

    /**
     * Points Rebuy's free shipping goal and copy at the tier the cart is in, then redraws the bar as live does
     * (fades it while Rebuy re-renders). Thresholds are in the presentment currency's cents.
     */
    updateFreeShippingBar() {
      const smartCart = window.Rebuy?.SmartCart;
      const settings = smartCart?.settings;
      const total = window.Rebuy?.Cart?.cart?.total_price;
      if (!settings?.free_shipping || !settings.language || typeof total !== 'number') return;

      const { freeShipping } = this.config;
      const rate = Number(window.Shopify?.currency?.rate) || 1;
      // parseInt as on live: a tier of "100.01" counts as 100
      const firstThreshold = Number.parseInt(rate * Number.parseInt(freeShipping.firstTier, 10) * 100, 10);
      const secondThreshold = Number.parseInt(rate * Number.parseInt(freeShipping.secondTier, 10) * 100, 10);

      if (total >= secondThreshold) {
        settings.free_shipping.minimum = secondThreshold;
        this.setShippingCopy(settings.language, freeShipping.reachedSecond, freeShipping.reachedSecond);
        this.fadeProgressBar(BAR_FADE_MS, () => this.setProgressFill('100%'));
        return;
      }

      if (total > firstThreshold) {
        settings.free_shipping.minimum = secondThreshold;
        this.setShippingCopy(settings.language, freeShipping.remainSecond, freeShipping.remainSecond);
        this.fadeProgressBar(BAR_FADE_MS, () => this.drawRemaining(secondThreshold - total, total, secondThreshold));
        return;
      }

      settings.free_shipping.minimum = firstThreshold;
      this.setShippingCopy(settings.language, freeShipping.remainFirst);
      this.later(() => {
        this.fadeProgressBar(0, () => this.drawRemaining(firstThreshold - total, total, secondThreshold));
      }, BAR_FADE_FIRST_TIER_MS);
    }

    /**
     * Swaps Rebuy's free shipping copy for the theme's tier copy. A blank setting falls back to Rebuy's own
     * copy, cached before the first swap, so an earlier tier's message never carries over.
     * @param {Object} language - Rebuy SmartCart settings.language
     * @param {string} remaining - shown while the goal is not reached
     * @param {string} [reached] - shown once it is
     */
    setShippingCopy(language, remaining, reached) {
      this.rebuyShippingCopy ??= {
        remaining: language.free_shipping_remaining,
        reached: language.free_shipping_reached,
      };

      language.free_shipping_remaining = remaining || this.rebuyShippingCopy.remaining;
      language.free_shipping_reached = reached || this.rebuyShippingCopy.reached;
    }

    /**
     * Dims the progress bar, runs draw after delay, then restores it.
     * @param {number} delay
     * @param {Function} draw
     */
    fadeProgressBar(delay, draw) {
      const wrapper = document.querySelector('#rebuy-cart .rebuy-cart__progress-bar-wrapper');
      if (wrapper) wrapper.style.opacity = '0.3';

      const finish = () => {
        draw();
        const current = document.querySelector('#rebuy-cart .rebuy-cart__progress-bar-wrapper');
        if (current) current.style.opacity = '1';
      };

      if (delay) {
        this.later(finish, delay);
      } else {
        finish();
      }
    }

    /**
     * @param {number} remaining - cents left to the threshold
     * @param {number} total - cart total in cents
     * @param {number} barMaximum - cents the full bar stands for (live always measures against the second tier)
     */
    drawRemaining(remaining, total, barMaximum) {
      const amount = document.querySelector('#rebuy-cart__progress-bar-meter-label span');
      const formatMoney = window.Rebuy?.SmartCart?.formatMoney;
      if (amount && typeof formatMoney === 'function') amount.innerHTML = formatMoney(remaining);

      const percentage = barMaximum > 0 ? Math.round((total / barMaximum) * 100) : 0;
      this.setProgressFill(`${percentage}%`);
    }

    /** @param {string} width */
    setProgressFill(width) {
      const fill = document.querySelector('#rebuy-cart .rebuy-cart__progress-bar-meter-fill');
      if (fill) fill.style.width = width;
    }

    /* ----- gift message ----- */

    /**
     * @param {CartLine} line
     * @param {string} tag
     * @returns {boolean}
     */
    lineHasTag(line, tag) {
      const tags = line.product?.tags || line.product_tags || line.tags || [];
      // Rebuy gives tags as one comma-separated string; live matched it with includes(), kept as is
      return tags.includes(tag);
    }

    /** Shows the gift messages above the cart items, and takes the gift out when nothing in the cart earns it. */
    async updateGiftMessage() {
      const { eligibleTag, giftSku, message, inCartMessage } = this.config.giftMessage;
      const items = this.rebuyItems;

      const qualifies = Boolean(eligibleTag) && items.some((line) => this.lineHasTag(line, eligibleTag));
      const gift = giftSku ? items.find((line) => line.sku === giftSku) : null;

      const parts = [];
      if (message && qualifies) parts.push(`<div class="gift-product-message">${message}</div>`);
      if (inCartMessage && qualifies && gift) parts.push(`<div class="gift-in-cart-message">${inCartMessage}</div>`);

      const target = document.querySelector('#rebuy-cart .rebuy-cart__flyout-gift-message');
      if (target) target.innerHTML = parts.join('');

      if (!gift || qualifies) return;
      await this.removeGift(gift.variant_id);
    }

    /** @param {string|number} variantId */
    async removeGift(variantId) {
      if (!variantId || !window.Cart) return;

      try {
        await window.Cart.update({ updates: { [variantId]: 0 } }, this);
        // Rebuy keeps its own copy of the cart — re-read it so the flyout drops the gift too
        await window.Rebuy?.Cart?.fetchCart?.();
      } catch (error) {
        console.error('rebuy-cart-addons: could not remove the gift', error);
      }
    }

    /* ----- gift with purchase (cart page) ----- */

    /**
     * Rebuy adds and removes the gift with purchase itself; the cart page is server-rendered, so reload
     * when the gift appears or disappears to list it (live behaviour).
     * @param {number} delay - lets Rebuy's request land before reading the cart
     */
    checkGiftWithPurchase(delay) {
      const sku = this.config.giftWithPurchaseSku;
      if (!sku) return;

      this.later(async () => {
        this.abortController?.abort();
        this.abortController = new AbortController();

        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
            signal: this.abortController.signal,
          });
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

          const cart = await response.json();
          const inCart = (cart.items || []).some((line) => line.sku === sku);
          const previous = this.giftWithPurchaseInCart;
          this.giftWithPurchaseInCart = inCart;

          if (previous === null || previous === inCart) return;
          this.later(() => window.location.reload(), CART_PAGE_RELOAD_MS);
        } catch (error) {
          if (error.name !== 'AbortError') console.error('rebuy-cart-addons: could not read the cart', error);
        }
      }, delay);
    }

    /* ----- membership-only cart ----- */

    /**
     * The membership has no shipping and earns nothing, so a cart holding only it hides the progress bars
     * (flyout and cart page) through the rebuy-membership-only-cart class on <body>.
     * @param {CartLine[]} [items]
     */
    updateMembershipOnly(items) {
      if (!Array.isArray(items)) return;

      const membershipId = String(this.config.membershipProductId || '').trim();
      const membershipOnly =
        items.length > 0 &&
        Boolean(membershipId) &&
        items.every((line) => String(line.product_id || line.product?.id || '') === membershipId);

      document.body.classList.toggle('rebuy-membership-only-cart', membershipOnly);
    }
  }

  customElements.define('rebuy-cart-addons', RebuyCartAddons);
}
